import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'
import { useBulkImageQuickActions } from '@/hooks/editor/useBulkImageQuickActions'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useSetToast } from '@/hooks/utility/useSetToast'

export const QuickActionsDrawer = () => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const syncCurrentPage = useSyncCurrentPageToEditorPages()
  const {
    applyBulkLightening,
    applyBulkRedness,
    applyBulkReset,
    isProcessing,
  } = useBulkImageQuickActions()

  // Factory function to create bulk action handlers with shared error handling and toast logic
  // Takes a bulk action function and a success translation key, returns a handler function
  const createBulkActionHandler = useCallback(
    (
      bulkActionFunction: () => Promise<{
        success: boolean
        successCount: number
        failureCount: number
        totalCount: number
        failedImages?: Array<{
          pageIndex: number
          itemId: string
          error: string
        }>
        error?: string
      }>,
      successTranslationKey: string,
    ) => {
      return async () => {
        // Sync current page items to editorPagesAtom (direct atomic update)
        await syncCurrentPage()

        // Execute the bulk action function and get the result
        const bulkActionResult = await bulkActionFunction()

        // Check if no images were found to process
        const noImagesFound = bulkActionResult.totalCount === 0
        if (noImagesFound) {
          setToast({
            text: t(
              'editor.menus.properties.elements.bulkQuickActions.noImagesFound',
            ),
          })
          return
        }

        // Check if all images failed to process
        const allImagesFailed = bulkActionResult.successCount === 0
        if (allImagesFailed) {
          setToast({
            text:
              bulkActionResult.error ||
              t(
                'editor.menus.properties.elements.bulkQuickActions.processingFailed',
              ),
          })
          return
        }

        // Check if all images succeeded (no failures)
        const allImagesSucceeded = bulkActionResult.failureCount === 0
        if (allImagesSucceeded) {
          // All images processed successfully - show success message with count
          setToast({
            text: t(successTranslationKey, {
              count: bulkActionResult.successCount,
            }),
          })
        } else {
          // Partial success - some images succeeded, some failed
          setToast({
            text: t(
              'editor.menus.properties.elements.bulkQuickActions.partialSuccess',
              {
                successCount: bulkActionResult.successCount,
                failureCount: bulkActionResult.failureCount,
                totalCount: bulkActionResult.totalCount,
              },
            ),
          })
        }
      }
    },
    [setToast, t, syncCurrentPage],
  )

  // Handler for lightening all images
  const handleLighteningAll = createBulkActionHandler(
    applyBulkLightening,
    'editor.menus.properties.elements.bulkQuickActions.imagesLightened',
  )

  // Handler for reducing redness in all images
  const handleRednessAll = createBulkActionHandler(
    applyBulkRedness,
    'editor.menus.properties.elements.bulkQuickActions.imagesRednessed',
  )

  // Handler for resetting all images
  const handleResetAll = createBulkActionHandler(
    applyBulkReset,
    'editor.menus.properties.elements.bulkQuickActions.imagesReset',
  )

  return (
    <Flex column padding="0 14px">
      <Text size="14px" color="gray2" weight="600">
        {t('editor.menus.properties.elements.bulkQuickActions.imagesLabel')}
      </Text>
      <Flex column gap="8px" align="center" margin="24px 0px" width="100%">
        <Button
          onClick={handleLighteningAll}
          disabled={isProcessing}
          width="100%"
          center
        >
          {t(
            'editor.menus.properties.elements.bulkQuickActions.lighteningAllButton',
          )}
        </Button>
        <Button
          onClick={handleRednessAll}
          disabled={isProcessing}
          width="100%"
          center
        >
          {t(
            'editor.menus.properties.elements.bulkQuickActions.rednessAllButton',
          )}
        </Button>
        <Button
          onClick={handleResetAll}
          disabled={isProcessing}
          width="100%"
          center
        >
          {t(
            'editor.menus.properties.elements.bulkQuickActions.resetAllButton',
          )}
        </Button>
      </Flex>
    </Flex>
  )
}
